import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from './DarkModeContext';
import { FPQuestionRow, FPRecordedAnswers, FPSubmitButton, fpColors, fmtFpDate, FP_GOLD,
         FP_NOTICE_ROLES, FPNotActiveNotice, FPPreviewBanner, fpRecordFor } from './FPAssessmentShared';

// FPWORDING: the server's refusal of an unexpected answer without its note (fp_assessment.py).
const FP_UNEXPECTED_NOTE_DETAIL = 'Your answer to this question is not the expected one. Please explain it in the note, or check the answer.';

const API = '/api';

// FPFRONTEND stage 1 (employee): the twelve fixed questions, each Yes/No with its own optional
// notes field, submitted TOGETHER in one POST - the backend validates the whole set at once and
// refuses a partial one, so a per-question save would be rejected anyway.
//
// The question list is fetched from GET /fp-assessment/questions/all rather than hard-coded here:
// the server owns the wording, and a client copy could drift from what the backend will actually
// record against each answer.
// FPTAB: `onApplicable` and `onPending` report upward so the Compliance page can decide whether to
// offer this as a tab at all, and whether to mark it as outstanding. The RULES stay here: this
// component is what asks the backend, so the parent never re-derives entity logic or invents its
// own idea of what "pending" means. Both are optional, so any caller that does not pass them (and
// the existing tests) behaves exactly as before.
//
// FPSWITCH: `viewerRole` is the signed-in account's role. While the backend reports
// fp_enabled !== true, the questionnaire is never offered: coo, admin and compliance see the
// "not active yet" notice (and their own existing answers, if any), everyone else sees nothing
// and the tab is not offered. Anything but a definite true counts as off.
// FPFIX2: `onSettled` (optional) is told once the eligibility and switch answers are in, so the
// Compliance page knows when "this does not apply" is final rather than still loading.
export default function FPEmployeeSection({ employeeId, onApplicable, onPending, viewerRole, onSettled }) {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const c = fpColors(isDarkMode);

  const [questions, setQuestions] = useState([]);
  const [record, setRecord] = useState(null);
  // Only an in-scope employee sees this at all. The backend already owns the rule and exposes it
  // self-service at /eligible/check, so the client asks rather than re-deriving entity logic.
  const [eligible, setEligible] = useState(null);
  const [fpOn, setFpOn] = useState(false);
  // FPPREVIEW: this account previews F&P while it is off for everyone else.
  const [preview, setPreview] = useState(false);
  // FPDUE: whether the server says an assessment is due (a pending one launched for the current
  // period). Starts false; a failed check stays false, so nothing reads as due without that answer.
  const [due, setDue] = useState(false);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const period = String(new Date().getFullYear());

  const load = useCallback(() => {
    if (!employeeId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/fp-assessment/questions/all`),
      axios.get(`${API}/fp-assessment/${employeeId}`).catch(() => ({ data: null })),
      axios.get(`${API}/fp-assessment/eligible/check/${employeeId}`).catch(() => ({ data: null })),
      // FPHIDE: the switch, read from its own endpoint on every load. A failure reads as off.
      axios.get(`${API}/fp-assessment/status/enabled`).catch(() => ({ data: null })),
    ])
      .then(([q, r, e, st]) => {
        setQuestions(q.data?.employee_questions || []);
        // FPFIX: the server answers with every period on file; this screen is about this year's.
        setRecord(fpRecordFor(r.data, period));
        // A failed eligibility check resolves to null, which hides the section rather than
        // showing a questionnaire to someone who may not be in scope.
        setEligible(e.data ? e.data.eligible === true : null);
        setDue(Boolean(e.data && e.data.due === true));
        // FPHIDE: on only when BOTH answers say a definite yes; anything else is off.
        setFpOn(Boolean(e.data && e.data.fp_enabled === true
                        && st && st.data && st.data.enabled === true));
        setPreview(Boolean(st && st.data && st.data.preview === true));
        setLoadFailed(false);
      })
      // A failed load must never render an empty questionnaire that looks answerable - the
      // employee would fill it in and the submit would fail against a question set we never read.
      .catch(() => { setQuestions([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [employeeId, period]);

  useEffect(() => { load(); }, [load]);

  const setAnswer = (qid, patch) =>
    setAnswers(prev => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...patch } }));

  const submit = () => {
    const missing = questions.filter(q => !answers[q.question_id]?.answer);
    if (missing.length) {
      setError(`Answer every question before submitting - ${missing.length} still outstanding.`);
      return;
    }
    // FPWORDING: an unexpected answer needs its note, as the server requires.
    const unexplained = questions.filter(q => q.expected_answer
      && answers[q.question_id].answer !== q.expected_answer
      && !(answers[q.question_id].notes || '').trim());
    if (unexplained.length) {
      setError(t('fp.unexpectedNote'));
      return;
    }
    setBusy(true);
    setError('');
    axios.post(`${API}/fp-assessment/${employeeId}/submit`, {
      period,
      responses: questions.map(q => ({
        question_id: q.question_id,
        answer: answers[q.question_id].answer,
        notes: (answers[q.question_id].notes || '').trim() || null,
      })),
    })
      .then(res => { setRecord(res.data); setDone(true); })
      .catch(err => {
        const d = err.response?.data?.detail;
        // FPWORDING: the server's unexpected-answer refusal, in the employee's language.
        if (d === FP_UNEXPECTED_NOTE_DETAIL) { setError(t('fp.unexpectedNote')); return; }
        setError(typeof d === 'string' ? d : 'Could not submit your assessment.');
      })
      .finally(() => setBusy(false));
  };

  const submitted = done || (record && record.status && record.status !== 'pending');

  // FPTAB: report upward. Declared BEFORE the early returns below, because a hook may not sit
  // after a conditional return - and because the parent must still be told "this does not apply"
  // in exactly the cases that return null, which is what hides the tab.
  //
  // APPLICABLE is the same condition the render gate uses: a definite yes from the backend's own
  // eligibility check. PENDING means in scope, loaded, and not yet submitted - the state this
  // component already renders the questionnaire for, rather than a second definition of "due".
  // FPSWITCH: while off, the tab is offered only to the notice roles and is never "pending".
  // FPDUE (2026-10-27, business decision): and PENDING (the "Due" badge) needs the server's word
  // that an assessment has actually been launched for this employee for the current period. With
  // none, nothing is due: the tab and the voluntary questionnaire stay, without the badge.
  const noticeViewer = FP_NOTICE_ROLES.includes(viewerRole);
  useEffect(() => {
    const inScope = Boolean(employeeId) && eligible === true;
    const applies = inScope && (fpOn || noticeViewer);
    if (onApplicable) onApplicable(applies);
    if (onPending) onPending(applies && fpOn && due && !loading && !loadFailed && !submitted);
    if (onSettled && !loading) onSettled(true);
  }, [employeeId, eligible, fpOn, due, noticeViewer, loading, loadFailed, submitted, onApplicable, onPending, onSettled]);

  if (!employeeId) return null;
  // Out of scope, or scope unknown: render nothing at all.
  if (!loading && eligible !== true) return null;

  // FPSWITCH: switched off. No questionnaire for anyone; the notice for the notice roles only.
  if (!loading && !loadFailed && !fpOn) {
    if (!noticeViewer) return null;
    return (
      <section style={{ marginBottom: '28px' }}>
        <FPNotActiveNotice text={t('fpSwitch.notActive')} isDarkMode={isDarkMode} />
        {record && record.status && record.status !== 'pending' && (
          <FPRecordedAnswers title="Your answers" rows={record.questionnaire_responses}
                             isDarkMode={isDarkMode} />
        )}
      </section>
    );
  }

  return (
    <section style={{ marginBottom: '28px' }}>
      <div style={{ fontSize: '15px', fontWeight: 700, color: c.text, marginBottom: '4px' }}>
        {t('fp.title')}
      </div>
      <div style={{ height: '3px', width: '36px', background: FP_GOLD, borderRadius: '2px', marginBottom: '12px' }} />
      {preview && fpOn && <FPPreviewBanner text={t('fpSwitch.preview')} isDarkMode={isDarkMode} />}

      {loading && <div style={{ fontSize: '13px', color: c.muted }}>Loading…</div>}

      {loadFailed && !loading && (
        <div style={{ fontSize: '13px', color: '#A63A3A' }}>
          Could not load the assessment. Please refresh - do not assume there is nothing to complete.
        </div>
      )}

      {!loading && !loadFailed && submitted && (
        <>
          <div style={{ fontSize: '13px', color: c.muted, marginBottom: '12px' }}>
            Submitted{record?.submitted_at ? ` on ${fmtFpDate(record.submitted_at)}` : ''}. Your
            answers are now with your manager for review.
          </div>
          <FPRecordedAnswers
            title="Your answers"
            rows={record?.questionnaire_responses}
            isDarkMode={isDarkMode}
          />
        </>
      )}

      {!loading && !loadFailed && !submitted && (
        <>
          <div style={{ fontSize: '13px', color: c.muted, marginBottom: '12px' }}>
            {t('fp.intro', { count: questions.length })}
          </div>
          {questions.map((q, i) => (
            <FPQuestionRow
              key={q.question_id}
              index={i + 1}
              questionId={q.question_id}
              questionText={q.question_text}
              value={answers[q.question_id]?.answer}
              notes={answers[q.question_id]?.notes}
              onChange={patch => setAnswer(q.question_id, patch)}
              isDarkMode={isDarkMode}
              expectedAnswer={q.expected_answer}
            />
          ))}
          {error && (
            <div style={{ fontSize: '12.5px', color: '#A63A3A', marginBottom: '10px' }}>{error}</div>
          )}
          <FPSubmitButton onClick={submit} busy={busy}>Submit assessment</FPSubmitButton>
        </>
      )}
    </section>
  );
}
